import { z } from "zod";

/**
 * The animations a video can carry (03, 05): motion-graphics work made in After Effects and exported with the
 * Bodymovin plugin as Lottie JSON. The catalogue is here so the chat, the plan and the composition name the same
 * thing; the files themselves live with the renderer (`apps/render/src/remotion/animations/`), which is the only
 * process that draws them.
 *
 * After Effects is a design tool here, never a render dependency (ADR 0032): a designer exports, the file is added
 * beside the others, and one row is added below. Nothing on any box runs Adobe software.
 *
 * The three seeds are the Airbnb Lottie sample animations (Apache 2.0), there to have something to choose from
 * until a designer's own work replaces them.
 */
export interface Animation {
  id: string;
  /** As a person says it. */
  name: string;
  /** One line: what it looks like, and where it sits. */
  note: string;
  /** How long one pass of it lasts. A shot longer than this loops it. */
  durationMs: number;
  /**
   * How much of the frame's width it wants. An animation drawn small inside its own frame, as icons are, needs a
   * larger box to read at all; the designer who exported it knows, so it is a row here and not a guess in `compose`.
   */
  widthFraction: number;
}

export const ANIMATIONS: readonly Animation[] = [
  { id: "heart", name: "Heart", note: "a heart bursts open, the way a like does", durationMs: 1_930, widthFraction: 0.9 },
  { id: "squares", name: "Nine squares", note: "nine squares tumbling in place, calm and geometric", durationMs: 5_870, widthFraction: 0.6 },
  { id: "watermelon", name: "Watermelon", note: "a slice of watermelon, drawn and playful", durationMs: 9_380, widthFraction: 0.7 },
];

export const animation = (id: string | undefined): Animation | undefined => ANIMATIONS.find((a) => a.id === id);
export const animationName = (id: string | undefined) => animation(id)?.name ?? "an animation";

/** What an `animation` overlay carries in a composition (05). */
export const AnimationOverlaySpec = z.object({
  animationId: z.string().min(1),
  /** Where it sits in the frame. */
  placement: z.enum(["top", "center", "bottom"]).default("center"),
  /** Its width as a fraction of the frame's. Height follows the animation's own proportions. */
  widthFraction: z.number().min(0.1).max(1).default(0.45),
  /** Play it again when it ends, for as long as the overlay lasts. */
  loop: z.boolean().default(true),
});
export type AnimationOverlaySpec = z.infer<typeof AnimationOverlaySpec>;
